import { Injectable, NgZone } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';

export interface PlaceAutocompleteSuggestionDto {
	placeId: string;
	text: string;
	mainText: string;
	secondaryText: string;
}

export interface AddressSearchAutocompleteResponse {
	success: boolean;
	errorMessage?: string;
	suggestions: PlaceAutocompleteSuggestionDto[];
}

@Injectable()
export class GooglePlacesService {
	private static readonly mapsLoadTimeoutMs = 20_000;
	private readonly searchQuery$ = new Subject<string>();
	private sessionToken: google.maps.places.AutocompleteSessionToken | null = null;
	private readonly predictionsByPlaceId = new Map<string, google.maps.places.PlacePrediction>();

	readonly addressSuggestions$: Observable<AddressSearchAutocompleteResponse> = this.searchQuery$.pipe(
		debounceTime(300),
		map((query) => query.trim()),
		distinctUntilChanged(),
		switchMap((query) => this.addressSearchAutocomplete(query))
	);

	constructor(private ngZone: NgZone) {}

	searchPlaces(query: string): void {
		this.searchQuery$.next(query ?? '');
	}

	/**
	 * Ends the Autocomplete billing session. Google only applies session SKUs when
	 * fetchFields() runs on a Place from PlacePrediction.toPlace() for this token.
	 */
	async completeSession(placeId?: string): Promise<void> {
		const prediction = placeId ? this.predictionsByPlaceId.get(placeId) : undefined;

		try {
			if (prediction) {
				const place = prediction.toPlace();
				await place.fetchFields({ fields: ['id', 'formattedAddress'] });
			}
		} catch (error) {
			console.error(error);
		} finally {
			this.resetSession();
		}
	}

	addressSearchAutocomplete(input: string): Observable<AddressSearchAutocompleteResponse> {
		const emptyResponse: AddressSearchAutocompleteResponse = {
			success: true,
			errorMessage: '',
			suggestions: []
		};

		if (!input) {
			this.resetSession();
			return of(emptyResponse);
		}

		return new Observable<AddressSearchAutocompleteResponse>((observer) => {
			const startedAt = Date.now();
			let disposed = false;
			let retryTimer: ReturnType<typeof setTimeout> | undefined;

			const emitUnavailable = () => {
				this.ngZone.run(() => {
					observer.next({
						success: false,
						errorMessage: 'Address search is unavailable right now. Please type your address manually.',
						suggestions: []
					});
					observer.complete();
				});
			};

			const search = () => {
				if (disposed) {
					return;
				}

				if (typeof google === 'undefined' || !google.maps?.places?.AutocompleteSuggestion) {
					if (Date.now() - startedAt >= GooglePlacesService.mapsLoadTimeoutMs) {
						emitUnavailable();
						return;
					}

					retryTimer = setTimeout(search, 100);
					return;
				}

				const sessionToken = this.ensureSessionToken();

				google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
					input,
					includedRegionCodes: ['ph'],
					sessionToken
				}).then(({ suggestions }) => {
					if (disposed) {
						return;
					}

					const predictions = suggestions
						.map((suggestion) => suggestion.placePrediction)
						.filter((prediction): prediction is google.maps.places.PlacePrediction => prediction !== null);

					this.predictionsByPlaceId.clear();
					for (const prediction of predictions) {
						this.predictionsByPlaceId.set(prediction.placeId, prediction);
					}

					this.ngZone.run(() => {
						observer.next({
							success: true,
							suggestions: predictions.map((prediction) => ({
								placeId: prediction.placeId,
								text: prediction.text.text,
								mainText: prediction.mainText?.text ?? prediction.text.text,
								secondaryText: prediction.secondaryText?.text ?? ''
							}))
						});
						observer.complete();
					});
				}).catch(() => {
						if (disposed) {
							return;
						}

						emitUnavailable();
					});
			};

			search();

			return () => {
				disposed = true;
				if (retryTimer) {
					clearTimeout(retryTimer);
				}
			};
		});
	}

	private ensureSessionToken(): google.maps.places.AutocompleteSessionToken {
		if (!this.sessionToken) {
			this.sessionToken = new google.maps.places.AutocompleteSessionToken();
		}

		return this.sessionToken;
	}

	private resetSession(): void {
		this.sessionToken = null;
		this.predictionsByPlaceId.clear();
	}
}
